import { baseApi } from "./api";
import type { Notification } from "@nicsmith/shared_types";

interface NotificationsListResponse {
  items: Notification[];
  lastEvaluatedKey?: Record<string, unknown>;
  unread_count: number;
}

interface UnreadCountResponse {
  unread_count: number;
}

interface MarkReadParams {
  notifId: string;
  sk: string;
}

interface BroadcastParams {
  title: string;
  message: string;
  link?: string | null;
}

export const notificationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listNotifications: builder.query<NotificationsListResponse, { limit?: number } | void>({
      query: (params) => ({
        url: "/notifications",
        params: { limit: params ? params.limit ?? 20 : 20 },
      }),
      providesTags: ["Notifications"],
    }),

    getNotificationUnreadCount: builder.query<UnreadCountResponse, void>({
      query: () => "/notifications/unread-count",
      providesTags: ["NotificationUnread"],
    }),

    markNotificationRead: builder.mutation<{ success: boolean }, MarkReadParams>({
      query: ({ notifId, sk }) => ({
        url: `/notifications/${notifId}/read`,
        method: "PUT",
        body: { sk },
      }),
      invalidatesTags: ["Notifications", "NotificationUnread"],
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        // Optimistically decrement the badge count immediately
        const patch = dispatch(
          notificationsApi.util.updateQueryData("getNotificationUnreadCount", undefined, (draft) => {
            if (draft.unread_count > 0) draft.unread_count -= 1;
          })
        );
        try { await queryFulfilled; } catch { patch.undo(); }
      },
    }),

    markAllNotificationsRead: builder.mutation<{ success: boolean }, void>({
      query: () => ({
        url: "/notifications/read-all",
        method: "PUT",
      }),
      invalidatesTags: ["Notifications", "NotificationUnread"],
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        // Optimistically zero the badge count immediately
        const patch = dispatch(
          notificationsApi.util.updateQueryData("getNotificationUnreadCount", undefined, (draft) => {
            draft.unread_count = 0;
          })
        );
        try { await queryFulfilled; } catch { patch.undo(); }
      },
    }),

    broadcastNotification: builder.mutation<{ message: string; succeeded: number; failed: number }, BroadcastParams>({
      query: (body) => ({
        url: "/notifications/broadcast",
        method: "POST",
        body,
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useListNotificationsQuery,
  useGetNotificationUnreadCountQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useBroadcastNotificationMutation,
} = notificationsApi;
